// src/pages/Suivi/SuiviPage.js
// Suivi d'une commande sans compte : la référence seule ne suffit pas,
// l'adresse email associée est également exigée.
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { orderService } from '../../services/api';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import './SuiviPage.css';

const STATUT_LABEL = {
  en_attente: 'En attente',
  confirme:   'Confirmée',
  expedie:    'Expédiée',
  livre:      'Livrée',
  annule:     'Annulée',
};

const ETAPES = ['confirme', 'expedie', 'livre'];

export default function SuiviPage() {
  useDocumentTitle('Suivre ma commande');

  const [form, setForm]       = useState({ reference: '', email: '' });
  const [commande, setCommande] = useState(null);
  const [erreur, setErreur]   = useState('');
  const [loading, setLoading] = useState(false);

  function handleChange(e) {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
    setErreur('');
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.reference.trim() || !form.email.trim()) {
      setErreur('Renseignez votre référence et votre adresse email.');
      return;
    }
    setLoading(true);
    setErreur('');
    setCommande(null);
    try {
      const data = await orderService.suivre(form.reference.trim(), form.email.trim());
      setCommande(data.order);
    } catch (err) {
      setErreur(err.message);
    } finally {
      setLoading(false);
    }
  }

  const etapeCourante = commande ? ETAPES.indexOf(commande.statut) : -1;

  return (
    <div className="suivi">
      <div className="container suivi__inner">
        <p className="suivi__overtitle">Espace commandes</p>
        <h1 className="suivi__titre">Suivre ma commande</h1>
        <hr className="divider-or" style={{ margin: '16px 0 32px' }} />

        <form className="suivi__form" onSubmit={handleSubmit}>
          <p className="suivi__intro">
            Saisissez la référence reçue à la validation de votre commande, ainsi
            que l'adresse email utilisée. Aucun compte n'est nécessaire.
          </p>

          <div className="suivi__row">
            <div className="suivi__field">
              <label htmlFor="reference">Référence de commande</label>
              <input
                id="reference" name="reference" value={form.reference}
                onChange={handleChange} placeholder="KEA-XXXXXXXX" autoComplete="off" required
              />
            </div>
            <div className="suivi__field">
              <label htmlFor="email">Adresse email</label>
              <input
                id="email" name="email" type="email" value={form.email}
                onChange={handleChange} placeholder="votre@email.com" required
              />
            </div>
          </div>

          {erreur && <p className="suivi__erreur" role="alert">{erreur}</p>}

          <button type="submit" className="btn-or suivi__submit" disabled={loading}>
            {loading ? 'Recherche...' : 'Voir ma commande'}
          </button>
        </form>

        {commande && (
          <div className="suivi__resultat">
            <div className="suivi__resultat-tete">
              <div>
                <span className="suivi__resultat-label">Commande</span>
                <strong className="suivi__resultat-ref">{commande.reference}</strong>
              </div>
              <span className={`suivi__badge suivi__badge--${commande.statut}`}>
                {STATUT_LABEL[commande.statut] || commande.statut}
              </span>
            </div>

            {commande.statut !== 'annule' && (
              <ol className="suivi__etapes">
                {ETAPES.map((etape, i) => (
                  <li key={etape} className={i <= etapeCourante ? 'suivi__etape suivi__etape--faite' : 'suivi__etape'}>
                    <span className="suivi__etape-point" aria-hidden="true" />
                    <span className="suivi__etape-label">{STATUT_LABEL[etape]}</span>
                  </li>
                ))}
              </ol>
            )}

            <div className="suivi__articles">
              {commande.items.map((item, i) => (
                <div key={i} className="suivi__article">
                  <span>{item.nom} × {item.quantite}</span>
                  <span className="suivi__article-prix">{item.sous_total.toFixed(2)} €</span>
                </div>
              ))}
              <div className="suivi__total">
                <span>Total</span>
                <strong>{commande.total.toFixed(2)} €</strong>
              </div>
            </div>

            {commande.adresse && (
              <p className="suivi__adresse">
                Livraison à {commande.adresse.prenom} {commande.adresse.nom} —{' '}
                {commande.adresse.rue}, {commande.adresse.code_postal} {commande.adresse.ville}
              </p>
            )}

            {commande.numero_suivi && (
              <p className="suivi__transporteur">
                Numéro de suivi transporteur : <strong>{commande.numero_suivi}</strong>
              </p>
            )}

            <p className="suivi__date">
              Commandée le {new Date(commande.date_commande).toLocaleDateString('fr-FR')}
            </p>
          </div>
        )}

        <p className="suivi__aide">
          Vous avez un compte ? <Link to="/connexion">Connectez-vous</Link> pour
          retrouver l'ensemble de vos commandes.
        </p>
      </div>
    </div>
  );
}
